import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { EnhancedTableHead, EnhancedTableToolbar, RenderChildrenBase, TableComponentProps } from './table.component';
import DeleteDialogComponent from '../dialog/delete-dialog.component';
import i18n from '../../i18n';
import './scss/table.component.scss'
import { Checkbox, CircularProgress, Collapse, Typography, TableCell, TableRow } from '@mui/material';
import { Group } from '../../models';

const ScrollTable = (props: Props) => {
    const { renderExtraCollapseComponent, showChecked, headCellList, selectType,
        compareSymbol = '_id', extraClass, list, isTableLoadingNext, isNoMoreData, filterCheckData,
        operateCollapseComponent, handleSort, handleCheck, handleDeleteAll, isTableLoading, renderToolBar,
        hasExpand, isActivateSummation, getDeleteDialogDesc, canDeleteSelected } = props;
    // 复选框选中的数据
    const [selected, setSelected] = React.useState<Data[]>(props.originSelect || []);
    // 操作列表折叠组件
    const [expandValue, setExpandValue] = React.useState<typeof operateCollapseComponent>({
        _id: '',
        activeIndex: 0
    })

    // 控制复选框批量删除提示
    const [deleteAllDialog, setDeleteAllDialog] = React.useState<boolean>(false);

    // 选中所有或者清除所有
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * 1.设置选中只选中当前页行数的数据
         * 2.全选按钮,选中取消,没有选中全选
         * 3.触发父类的 handleCheck方法
         * $ 4.过滤不需要被勾选在的数据
         */
        if (event.target.checked) {
            const currentRowsSelected = list;
            // $ 数据过滤
            const newSelected = props.filterCheckData?.(currentRowsSelected) || currentRowsSelected;
            props.handleCheck?.(newSelected);
            setSelected(list);
            return;
        }
        props.handleCheck?.([]);
        setSelected([]);
    };

    /**
    * 操作折叠组件展开
    * @param _id 表格_id
    * @param activeKey 表格索引
    * @param isExpand 展开的值
    */
    const handleExpand = (_id: string, activeKey: number, isExpand?: boolean) => {
        // 存在展开的项时关闭
        if (isExpand) { setExpandValue({ _id: '', activeIndex: -1 }) }
        // 不存在则展开
        else { setExpandValue({ _id, activeIndex: activeKey }) }
    }

    // 加载更多数据
    const handleLoadMore = () => {
        props.handleLoadMore?.();
    };

    // 单机选中单行
    const handleClick = (event: React.MouseEvent<unknown>, row: Data) => {
        /**
         * 1.这里单选状态和复选状态
         * 2.保存选中的数据
         * 3.过滤掉不需要的字段勾选的
         */
        // 不可以编辑 (不给高亮选中状态)
        if (showChecked === 0) return;
        // 选中的数据处理
        const selectedItem = selected.some((item) => {
            if (item[compareSymbol]) {
                return item[compareSymbol] === row[compareSymbol];
            }
            return false;
        });

        let newSelected: Data[] = [];
        if (selectType === 'RADIO') {
            if (!selectedItem) {
                if (filterCheckData?.([row])?.length === 0) return;
                newSelected = [row];
            } else {
                newSelected = [];
            }
        } else {
            if (!selectedItem) {
                newSelected = [...selected, row];
            } else {
                newSelected = selected.filter((item) => {
                    return !(item[compareSymbol] === row[compareSymbol]);
                });
            }
        }
        const finalSelected = filterCheckData?.(newSelected.filter((item) => !!item)) || newSelected;
        handleCheck?.(finalSelected || []);
        setSelected(finalSelected || newSelected);
    };

    // 关闭或者开启 批量删除弹窗
    const toggleDialog = (status: boolean) => {
        setDeleteAllDialog(status);
    }

    // 复选框是否可用
    function checkDisabled(row: any) {
        /**
         * 1.过滤函数不存在返回false
         * 2.过滤函数存在数组，说没没用不符合数据 返回false
         */
        return filterCheckData ? filterCheckData?.([row])?.length ? false : true : false
    }

    // 批量删除确定按钮
    async function handleConfirmDelete(status: number) {
        /**
         * 1.触发父级删除事件
         * 2.清空选中的复选内容
         * 3.关闭提示框
         */
        if (status === 1) {
            if (!canDeleteSelected || canDeleteSelected(selected)) {
                await handleDeleteAll?.(selected);
            }
            setSelected([]);
            setDeleteAllDialog(false);
        }
    }

    // 判断是否被选中了 当前行
    const isSelected = (row: Data) => selected.findIndex((item) => {
        if (item[compareSymbol]) {
            return item[compareSymbol] === row[compareSymbol];
        }
        return false;
    }) !== -1;


    // 渲染表头
    const renderTableHead = () => {
        return (
            <tr>
                <td>
                    <table>
                        <thead>
                            <EnhancedTableHead
                                headCellList={headCellList}
                                showChecked={showChecked}
                                numSelected={selected.length}
                                onSelectAllClick={handleSelectAllClick}
                                rowCount={selected.length}
                                handleSort={handleSort}
                                selectType={selectType}
                                hasExpand={hasExpand}
                                isShowSerialNum={isActivateSummation}
                            />
                        </thead>
                    </table>
                </td>
            </tr>
        )
    }

    // 渲染表体
    const renderTableBody = (index: number, row: any) => {
        const isItemSelected = isSelected(row);
        const labelId = `enhanced-table-checkbox-${index}`;
        // 是否展开折叠组件列表
        const isExpandValue = expandValue?.activeIndex === index && expandValue?._id === (row as Group)?._id;
        return (
            <>
                <td>
                    <table>
                        <tbody>
                            <TableRow
                                hover
                                onClick={(event) => props.isUseFold && handleClick(event, row)}
                                selected={isItemSelected}
                                className='borderBottom-set'
                            >
                                {/* 复选框 */}
                                {
                                    props.showChecked !== 0 && (
                                        <TableCell
                                            size='small'
                                            padding='checkbox'
                                            onClick={(event: any) => props.isUseFold && handleClick(event, row)}
                                        >
                                            <Checkbox
                                                disabled={!!checkDisabled?.(row)}
                                                checked={isItemSelected}
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </TableCell>
                                    )
                                }
                                {
                                    (props.isActivateSummation && props.list.length > 0) ?
                                        <TableCell
                                            size='small'
                                            style={{
                                                borderTop: 0,
                                                width: '3%'
                                            }}
                                        >
                                            {index + 1}
                                        </TableCell>
                                        : null
                                }

                                {/* 子元素 */}
                                {
                                    <RenderChildrenBase
                                        {...{
                                            headCellList: props.headCellList,
                                            row,
                                            index,
                                            hasExpand: props.hasExpand,
                                            isExpandValue,
                                            onHandleExpand: handleExpand
                                        }}
                                    />
                                }
                            </TableRow>
                            {/* 折叠的组件 用于集团列表页展示授权点和授权集合相关信息 */}
                            {
                                renderExtraCollapseComponent &&
                                <TableRow style={{}}>
                                    <TableCell
                                        style={{
                                            padding: '0px 10px',
                                            borderTop: 0,
                                        }}
                                        colSpan={10}
                                    >
                                        <Collapse
                                            in={isExpandValue}
                                            timeout={450}
                                            unmountOnExit
                                            className='collapse-class'
                                        >
                                            {renderExtraCollapseComponent(row, index)}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            }
                        </tbody>
                    </table>
                </td>
            </>
        )
    }

    return (
        <div className='scrollTable'>
            {
                //  $ 复选框删除提示框 (只有1的时候才显示)
                [1].includes(showChecked) && selectType !== 'RADIO' && (
                    <EnhancedTableToolbar
                        {...{
                            numSelected: selected.length,
                            handleDelete: () => { toggleDialog(true) },
                            renderToolBar,
                            renderToolBarRight: () => props.renderToolBarRight?.(selected),
                        }}
                    />
                )
            }
            {/* loading 加载框显示 */}
            {
                isTableLoading && (
                    <div className='loadingBody'>
                        <CircularProgress sx={(theme) => { return { color: theme.palette.primary.main } }} />
                    </div>
                )
            }
            <TableVirtuoso
                // key data变动后endReached有时不会触发 需要重新渲染
                key={isTableLoading ? 'loading' : 'data'}
                className={`tableComponent ${extraClass}`}
                // 数据
                data={isTableLoading ? [] : list}
                // 提前加载
                increaseViewportBy={20}
                // 加载更多
                endReached={handleLoadMore}
                // 表头
                fixedHeaderContent={renderTableHead}
                // 表尾
                fixedFooterContent={() => !isTableLoading ? (
                    <Footer
                        isLoading={isTableLoadingNext}
                        isNoMoreData={isNoMoreData}
                        isNoData={list.length === 0}
                    />) : null}
                // 行内容
                itemContent={renderTableBody}
                style={{ height: props.tableHeight || 'calc(100vh - 350px)', width: '100%' }}
            />
            <div className='scrollTableTotal'>
                <Typography className='total'>total: {list.length} </Typography>
            </div>
            {/* 是否批量删除所选择项目 */}
            <DeleteDialogComponent
                {...{
                    title: i18n.t('general_text_tips'),
                    desc: getDeleteDialogDesc?.(selected),
                    confirmDisabled: canDeleteSelected ? !canDeleteSelected(selected) : false,
                    open: deleteAllDialog,
                    onCancel: () => setDeleteAllDialog(false),
                    onConfirm: () => handleConfirmDelete(1),
                    onClose: () => setDeleteAllDialog(false),
                }}
            />
        </div>
    );
};

export default ScrollTable;

type Props = {
    // 表格高度
    tableHeight?: string;
    // 加载更多数据
    handleLoadMore: () => Promise<void>;
    // 是否加载中
    isTableLoadingNext: boolean
    // 是否没有更多数据
    isNoMoreData: boolean;
} & TableComponentProps

export interface Data {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}


function Footer({ isLoading, isNoMoreData, isNoData }: { isLoading: boolean, isNoMoreData: boolean, isNoData: boolean }) {
    return (
        <tr>
            <td>
                <div className='FCC scrollTableFooter'>
                    {
                        isLoading &&
                        <CircularProgress color='primary' />
                    }
                    {
                        isNoMoreData &&
                        <Typography>{i18n.t('google_text_no_more_data')}</Typography>
                    }
                    {
                        isNoData &&
                        <Typography>  {i18n.t('table_component_text_no_or_more_data')}</Typography>
                    }

                </div>
            </td>
        </tr>
    )
}